import React, { useState, useEffect, useRef, useContext } from 'react';
import './GroupCreationModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import SelectInput from '../SelectInput/SelectInput';
import UserCard from '../UserCard/UserCard';
import ActionButtons from '../ActionButtons/ActionButtons';
import BackendSearchSelect from '../BackendSearchSelect/BackendSearchSelect';
import baseApi from '../../common/api/base';
import convenienceApi from '../../common/api/convenience';
import logComponent from '../../common/logComponent';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';

export default function GroupCreationModal({ adminUser, addGroup: propAddGroup, hide, isVisible, groups }) {
  const environmentContext = useContext(EnvironmentContext);
  const { activeUser: user } = environmentContext;
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [fields, setFields] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState('initial');
  const [totalCorrespondents, setTotalCorrespondents] = useState(0);
  const [totalImpersonators, setTotalImpersonators] = useState(0);
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState(false);
  const newGroupRef = useRef({
    name: null,
    canCorrespond: null,
    canImpersonate: null,
    groupMembers: null,
    checkInSurveyId: null
  });

  const setNewGroupRef = (el) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal setNewGroupRef'); }
    newGroupRef.current = el;
  }

  const [newGroup, setNewGroup] = useState({
    name: null,
    canCorrespond: null,
    canImpersonate: null,
    groupMembers: null,
    checkInSurveyId: null
  });
  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const lastTabRef = useRef(null);
  const actionButtons = useRef(null);

  const setModalRef = (el) => { modalRef.current = el }
  const setFirstTabRef = (el) => { firstTabRef.current = el }
  const setLastTabRef = (el) => { lastTabRef.current = el }
  const formIsInvalid = isValid === false;
  const { facilityId, statusSolutionsId, corporationId } = adminUser;

  if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal render'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal onMount'); }
    if (firstTabRef.current)
      firstTabRef.current.focus()
    loadCheckInSurveys();
  }, []);

  useEffect(() => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal useEffect [status]'); }
    // handle submission failure
    if (status === 'failure' && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [status]);

  const showValidationErrors = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal showValidationErrors'); }
    setShowErrors(true);
  }

  const loadCheckInSurveys = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal loadCheckInSurveys'); }

    let adminUserType = adminUser.groupTypeString;

    setSurveys(null);
    setSurveysLoading(true);
    setSurveysError(false);


    let id = 0;
    if (adminUserType) {
      switch (adminUserType) {
        case 'Status Solutions':
          break;
        case 'Corporate':
          break;
        case 'Facility':

          if (facilityId) {
            baseApi.Survey.GetAllCheckInSurveysForFacility(facilityId)
              .then(
                result => {
                  let defaultChoice = [{ key: '', value: '' }];
                  let availableSurveys = result.data.map(survey => {
                    return {
                      key: survey.surveyName + ' (' + survey.ownerName + ')', 
                      value: survey.surveyId
                    };
                  });
                  let newSurveys = defaultChoice.concat(availableSurveys);
                  setSurveys(newSurveys);
                  setSurveysLoading(false);
                  setSurveysError(false);
                },
                error => {
                  setSurveys(null);
                  setSurveysLoading(false);
                  setSurveysError(true);
                }
              );
          }
          break;
        default:
          break;
      }
    }
  }

  const hideValidationErrors = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal hideValidationErrors'); }
    setShowErrors(false);
  }

  const validateField = (field) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal validateField'); }

    let newGroupThis = { ...newGroupRef.current };
    newGroupThis[field.name] = field;
    setNewGroupRef(newGroupThis);
    setNewGroup(newGroupThis); //doing this as both ref and state so it will have the values updated when I need but still refresh the page when needed

  }

  const submit = (e) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal submit: e=' + e); }
    const { facilityId, statusSolutionsId, corporationId } = adminUser;
    // check all necessary fields
    let requiredFieldList = ['name', 'canCorrespond'];

    if (facilityId == null && statusSolutionsId == null && corporationId == null) {
      requiredFieldList = ['name'];
    }
    if (facilityId == null && (statusSolutionsId != null || corporationId != null)) {
      requiredFieldList = ['name', 'canImpersonate'];
    }



    let isValid = requiredFieldList.reduce((prev, curr) => prev && newGroup[curr] && newGroup[curr].isValid, true);

    if (!isValid) {
      handleInvalidSubmission();
    } else {
      handleValidSubmission();
    }
  }

  const handleValidSubmission = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal handleValidSubmission'); }
    hideValidationErrors();
    addGroup();
    setIsValid(true);
  }

  const handleInvalidSubmission = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal handleInvalidSubmission'); }
    showValidationErrors();
    if (modalRef.current) modalRef.current.scrollTop = 0;
    if (firstTabRef.current) firstTabRef.current.focus();
    setIsValid(false);
  }

  const addGroup = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal addGroup'); }
    const { facilityId, statusSolutionsId, corporationId } = adminUser;
    let groupToSubmit = {};

    setStatus('pending');

    for (let prop in newGroup) {
      if (prop == 'canCorrespond' && facilityId == null) {
        groupToSubmit[prop] = false;
      } else if (prop == 'canImpersonate' && statusSolutionsId == null && corporationId == null) {
        groupToSubmit[prop] = false;
      } else if (prop == 'canViewSurveyResponses' && facilityId == null) {
        groupToSubmit[prop] = false;
      } else if (prop == 'checkInSurveyId' && facilityId == null) {
        groupToSubmit[prop] = 0;
      } else if (prop == 'groupMembers' && !newGroup[prop]) {
        groupToSubmit[prop] = [];
      } else {
        groupToSubmit[prop] = newGroup[prop].value;
      }
    }

    // Add necessary fields
    groupToSubmit.facilityId = facilityId;
    groupToSubmit.statusSolutionsId = statusSolutionsId;
    groupToSubmit.corporationId = corporationId;
    groupToSubmit.canViewSurveyResponses = groupToSubmit.canViewSurveyResponses == null ? false : groupToSubmit.canViewSurveyResponses;

    convenienceApi.createGroup(groupToSubmit)
      .then(
        result => {
          if (newGroupRef.checkInSurveyId === true && groupToSubmit.groupMembers.some(newMember => newMember.userId === user.userId)) {
            environmentContext.setShouldUpdateCheckInSurvey(true);
          }
          setStatus('success');
          propAddGroup(result);
          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const handleTab = (e) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal handleTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || shiftKey) return;

    e.preventDefault();
    firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    lastTabRef.current.focus();
  }

  const updateTotals = (users) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal updateTotals'); }
    updateTotalCorrespondents(users);
    updateTotalImpersonators(users);
  }

  const updateTotalCorrespondents = (users) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal updateTotalCorrespondents'); }
    let count = users.reduce((prev, curr) => {
      return (curr.canCorrespond ? prev + 1 : prev);
    }, 0);
    setTotalCorrespondents(count);
  }

  const updateTotalImpersonators = (users) => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal updateTotalImpersonators'); }
    let count = users.reduce((prev, curr) => {
      return (curr.canImpersonate ? prev + 1 : prev);
    }, 0);

    setTotalImpersonators(count);
  }

  const startModalExit = () => {
    if (logComponent('GroupCreationModal')) { console.debug('GroupCreationModal startModalExit'); }
    setModalShouldExit(true);
  }


  return (
    <Modal isVisible={isVisible} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true} >
      <div className="Group-Creation-Modal">
        <h3 className="Group-Creation-Modal__heading">Create new group</h3>
        <p className={'Group-Creation-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'Group-Creation-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, we were unable to complete the action.  Group was not created.  Please try again.</p>
        <form className="Group-Creation-Modal__form">
          <TextInput
            label="Group name"
            name="name"
            rules={{
              required: true,
              notIn: groups.map(group => group.name)
            }}
            errorMessages={{
              required: 'Please provide a name for the group',
              notIn: 'A group already exists with that name, please choose a unique name.'
            }}
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField}
            ref={setFirstTabRef}
            onKeyDown={handleShiftTab} />
          {
            facilityId &&
            <CheckboxInput
              label="Can correspond with individuals' family?"
              name="canCorrespond"
              instructions={totalCorrespondents > 0 ? `Note: ${totalCorrespondents} member${totalCorrespondents > 1 ? 's' : ''} of this group ${totalCorrespondents > 1 ? 'are' : 'is'} enabled for correspondence manually` : ''}
              explanation="Individuals in this group may correspond with individuals' family"
              validate={validating}
              onValidate={validateField} />
          }
          {
            (statusSolutionsId || corporationId) &&
            <CheckboxInput
              label="Can Mimic other MIMI users?"
              name="canImpersonate"
              instructions={totalImpersonators > 0 ? `Note: ${totalImpersonators} member${totalImpersonators > 1 ? 's' : ''} of this group ${totalImpersonators > 1 ? 'are' : 'is'} enabled for mimicking manually` : ''}
              explanation="Individuals in this group may mimic other MIMI users associated with their organization"
              initialValue={false}
              validate={validating}
              onValidate={validateField} />
          }
          {
            facilityId &&
            <CheckboxInput
              label="Can view survey responses"
              name="canViewSurveyResponses"
              instructions={totalImpersonators > 0 ? `Note: ${totalImpersonators} member${totalImpersonators > 1 ? 's' : ''} of this group ${totalImpersonators > 1 ? 'are' : 'is'} enabled for viewing survey responses` : ''}
              explanation="Individuals in this group may view survey responses in MIMI."
              initialValue={false}
              validate={validating}
              onValidate={validateField} />
          }
          {
            facilityId &&
            <SelectInput
              label="Check-In survey"
              name="checkInSurveyId"
              items={surveys}
              errorMessage="Please select a check-in survey for this group.  If not is selected, it will use the default survey"
              showError={formIsInvalid}
              rules={null}
              validate={validating}
              onValidate={validateField}
            />
          }
          <BackendSearchSelect
            label="Select group members"
            itemKeyField="userId"
            itemPluralTerm="users"
            searchFunction={facilityId != null ? convenienceApi.searchFacilityUsers : corporationId != null ? convenienceApi.searchCorporationUsers : convenienceApi.searchStatusSolutionsUsers}
            itemComponent={UserCard}
            itemComponentPropName="user"
            name="groupMembers"
            validate={validating}
            onValidate={validateField}
            onSelect={updateTotals}
          />
          <ActionButtons
            ref={actionButtons}
            mainAction={submit}
            mainActionText="Save changes"
            cancel={startModalExit}
            cancelText="Cancel"
            currentStatus={status} />
        </form>
      </div>
    </Modal>
  );
}